import type { PivotGrandTotalsEnum } from '@ztechapps/common.contract'
import type { PivotHeaderCell, PivotLeafColumn, PivotNode, PivotResult } from './types'
import { PIVOT_GRAND_TOTAL_LABEL, PIVOT_TOTAL_SUFFIX } from './constants'
import { hasPivotGrandTotalColumns } from './pivotGrandTotals.utils'
import { toPivotMeasureLabel } from './toPivotMeasureLabel'

/** Lists pivot column headers and leaf measure columns. */
export const listPivotColumns = ({
  result,
  showSubtotals,
  showGrandTotals,
}: {
  result: PivotResult
  showSubtotals: boolean
  showGrandTotals: PivotGrandTotalsEnum
}): { headerRows: PivotHeaderCell[][]; leafColumns: PivotLeafColumn[] } => {
  const depth = result.columnFields.length
  const leafColumns: PivotLeafColumn[] = []
  const leafChains: PivotNode[][] = []
  const pushMeasures = (node: PivotNode, chain: PivotNode[]): void => {
    result.measures.forEach((_, measureIndex) => {
      leafColumns.push({ key: `${node.key}::${measureIndex}`, columnKey: node.key, measureIndex })
      leafChains.push(chain)
    })
  }
  const visit = (node: PivotNode, ancestors: PivotNode[]): void => {
    const chain = [...ancestors, node]
    if (node.depth === depth) {
      pushMeasures(node, chain)
      return
    }
    node.children.forEach((child) => visit(child, chain))
    if (showSubtotals) pushMeasures(node, chain)
  }
  if (depth === 0) {
    pushMeasures(result.columnRoot, [result.columnRoot])
  } else {
    result.columnRoot.children.forEach((child) => visit(child, [result.columnRoot]))
    if (hasPivotGrandTotalColumns(showGrandTotals)) pushMeasures(result.columnRoot, [result.columnRoot])
  }

  const headerRows: PivotHeaderCell[][] = []
  const totalRows = depth + 1
  for (let row = 0; row < depth; row++) {
    const cells: PivotHeaderCell[] = []
    let index = 0
    while (index < leafColumns.length) {
      const chain = leafChains[index]
      const node = chain[chain.length - 1]
      const nodeDepth = chain.length - 1
      if (nodeDepth < row) {
        index += 1
        continue
      }
      if (nodeDepth === row) {
        const isRoot = node.key === result.columnRoot.key
        // A subtotal/Grand Total node's own `pushMeasures` call pushed one leaf column per
        // measure (same node, same chain) — span across all of them, like a regular member
        // header does, instead of a hardcoded single column. The measure row (`:99-110`) always
        // emits its own cell per leaf column, including these, so the total header must always
        // stop one row above the measure row — never spanning down into it, even with a single
        // measure — or the two rows disagree on how many columns the total occupies.
        const span = result.measures.length
        cells.push({
          key: `header:${row}:${node.key}`,
          label: isRoot ? PIVOT_GRAND_TOTAL_LABEL : `${node.member.label} ${PIVOT_TOTAL_SUFFIX}`,
          colSpan: span,
          rowSpan: totalRows - row - 1,
          isMeasure: false,
          // a subtotal at header row `r` totals the group one level up, grouped by
          // `columnFields[r - 1]` — never `columnFields[r]`, which is one field too deep
          field: isRoot ? undefined : result.columnFields[row - 1]?.field,
          memberLabel: isRoot ? undefined : node.member.label,
          isBlank: isRoot ? undefined : node.member.isBlank,
          isTotal: !isRoot,
        })
        index += span
        continue
      }
      const ancestor = chain[row + 1]
      let span = 0
      while (
        index + span < leafColumns.length &&
        leafChains[index + span].length - 1 > row &&
        leafChains[index + span][row + 1]?.key === ancestor.key
      ) {
        span += 1
      }
      cells.push({
        key: `header:${row}:${ancestor.key}`,
        label: ancestor.member.label,
        colSpan: span,
        rowSpan: 1,
        isMeasure: false,
        field: result.columnFields[row]?.field,
        isBlank: ancestor.member.isBlank,
      })
      index += span
    }
    headerRows.push(cells)
  }
  headerRows.push(
    leafColumns.map((leaf) => {
      const measure = result.measures[leaf.measureIndex]
      return {
        key: `measure:${leaf.key}`,
        label: toPivotMeasureLabel(measure),
        colSpan: 1,
        rowSpan: 1,
        isMeasure: true,
      }
    })
  )
  return { headerRows, leafColumns }
}
